import { Component } from '@theme/component';

/**
 * ProductHotspotsSection
 *
 * Theme-block architecture note:
 * Because _hotspot-product is a theme block (lives in /blocks/ and is rendered
 * via {% content_for 'blocks' %}), the section cannot iterate `section.blocks`
 * to read each block's settings — that returns empty settings for theme blocks.
 * Instead, each block renders its own four pieces (dot, mobile tile, panel dot,
 * card) into a hidden "block source" container. On connect, this component
 * walks that container and distributes those pieces into the correct named
 * regions of the section.
 */
export class ProductHotspotsSection extends Component {
  #touchStartX = null;
  #touchEndX = null;
  #scrollTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    this.#distributeBlockParts();
    this.#initializeVariantCards();
    this.#bindEvents();
    this.#setInitialState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('touchstart', this.#handleTouchStart);
    this.removeEventListener('touchend', this.#handleTouchEnd);
  }

  #distributeBlockParts() {
    const stage = this.querySelector('.shop-the-look__stage');
    const panelDots = this.querySelector('.shop-the-look__panel-dots');
    const mobilePicker = this.querySelector('.shop-the-look__mobile-picker');
    const cards = this.querySelector('.shop-the-look__cards');
    const source = this.querySelector('.shop-the-look__block-source');

    if (!stage || !panelDots || !mobilePicker || !cards || !source) return;

    const hotspots = source.querySelectorAll('.shop-the-look-hotspot');

    hotspots.forEach((hotspot) => {
      const dot = hotspot.querySelector('.shop-the-look-hotspot__dot');
      const mobileTile = hotspot.querySelector('.shop-the-look-hotspot__mobile-tile');
      const panelDot = hotspot.querySelector('.shop-the-look-hotspot__panel-dot');
      const card = hotspot.querySelector('.shop-the-look-hotspot__card');

      // Copy position from the wrapper's custom props onto the dot directly.
      // The wrapper carries --hotspot-x / --hotspot-y; once we move the dot out,
      // the cascade breaks, so we set left/top on the dot itself.
      if (dot) {
        const x = hotspot.style.getPropertyValue('--hotspot-x');
        const y = hotspot.style.getPropertyValue('--hotspot-y');
        if (x) dot.style.left = x;
        if (y) dot.style.top = y;
        stage.appendChild(dot);
      }
      if (mobileTile) mobilePicker.appendChild(mobileTile);
      if (panelDot) panelDots.appendChild(panelDot);
      if (card) cards.appendChild(card);
    });
  }

  #bindEvents() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.addEventListener('touchend', this.#handleTouchEnd);

    const mobilePicker = this.querySelector('.shop-the-look__mobile-picker');
    if (mobilePicker) {
      mobilePicker.addEventListener('scroll', this.#handleScroll, { passive: true });
    }
  }

  #handleScroll = () => {
    if (this.#scrollTimeout) {
      clearTimeout(this.#scrollTimeout);
    }
    
    this.#scrollTimeout = setTimeout(() => {
      const mobilePicker = this.querySelector('.shop-the-look__mobile-picker');
      if (!mobilePicker || mobilePicker.offsetParent === null) return;
      
      const pickerRect = mobilePicker.getBoundingClientRect();
      const pickerCenter = pickerRect.left + (pickerRect.width / 2);
      const tiles = Array.from(mobilePicker.querySelectorAll('.shop-the-look-hotspot__mobile-tile'));
      
      let closestTile = null;
      let minDistance = Infinity;
      
      tiles.forEach((tile) => {
        const tileRect = tile.getBoundingClientRect();
        const tileCenter = tileRect.left + (tileRect.width / 2);
        const distance = Math.abs(pickerCenter - tileCenter);
        
        if (distance < minDistance) {
          minDistance = distance;
          closestTile = tile;
        }
      });
      
      if (closestTile) {
        const hotspotId = closestTile.dataset.shopLookTrigger;
        if (hotspotId) {
          const activeCard = this.querySelector(`.shop-the-look-hotspot__card.is-active`);
          if (!activeCard || activeCard.dataset.shopLookCard !== hotspotId) {
            this.setActiveHotspot(hotspotId, true);
          }
        }
      }
    }, 50); // Small debounce
  };

  #handleTouchStart = (event) => {
    // Ignore swipes that start on the horizontally scrollable picker
    if (event.target.closest('.shop-the-look__mobile-picker')) {
      this.#touchStartX = null;
      return;
    }
    this.#touchStartX = event.changedTouches[0].screenX;
  };

  #handleTouchEnd = (event) => {
    if (this.#touchStartX === null) return;
    this.#touchEndX = event.changedTouches[0].screenX;
    this.#handleSwipe();
  };

  #handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.#touchStartX - this.#touchEndX;

    if (Math.abs(diff) < swipeThreshold) return;

    // Get unique hotspot IDs in order
    const triggers = this.#getTriggers();
    const hotspots = [];
    triggers.forEach(t => {
      const id = t.dataset.shopLookTrigger;
      if (id && !hotspots.includes(id)) hotspots.push(id);
    });

    if (hotspots.length <= 1) return;

    // Find currently active card
    const cards = this.#getCards();
    const activeCard = cards.find(card => card.classList.contains('is-active'));
    if (!activeCard) return;

    const currentId = activeCard.dataset.shopLookCard;
    const currentIndex = hotspots.indexOf(currentId);
    if (currentIndex === -1) return;

    let newIndex = currentIndex;

    if (diff > 0) {
      // Swiped left (finger moved right-to-left) -> next item
      newIndex = (currentIndex + 1) % hotspots.length;
    } else {
      // Swiped right (finger moved left-to-right) -> previous item
      newIndex = (currentIndex - 1 + hotspots.length) % hotspots.length;
    }

    this.setActiveHotspot(hotspots[newIndex]);
  }

  #getTriggers() {
    return [...this.querySelectorAll('[data-shop-look-trigger]')];
  }

  #getCards() {
    return [...this.querySelectorAll('[data-shop-look-card]')];
  }

  #initializeVariantCards() {
    this.#getCards().forEach((card) => {
      const variant = this.#getSelectedVariant(card);
      if (variant) {
        this.#applyVariantToCard(card, variant);
      }
    });
  }

  #getCardVariants(card) {
    if (card._shopLookVariants) return card._shopLookVariants;

    const json = card.querySelector('[data-shop-look-variants]');
    if (!json?.textContent) return [];

    try {
      card._shopLookVariants = JSON.parse(json.textContent);
      return card._shopLookVariants;
    } catch {
      return [];
    }
  }

  #getCardSelectedOptions(card) {
    return [card.dataset.selectedOption1 || '', card.dataset.selectedOption2 || '', card.dataset.selectedOption3 || ''];
  }

  #getSelectedVariant(card) {
    const variants = this.#getCardVariants(card);
    const selectedOptions = this.#getCardSelectedOptions(card);

    return (
      variants.find((variant) =>
        selectedOptions.every((optionValue, index) => !optionValue || variant[`option${index + 1}`] === optionValue)
      ) || variants[0]
    );
  }

  #findBestVariant(card, position, value) {
    const variants = this.#getCardVariants(card);
    const selectedOptions = this.#getCardSelectedOptions(card);
    selectedOptions[position - 1] = value;

    const exactMatch = variants.find((variant) =>
      selectedOptions.every((optionValue, index) => !optionValue || variant[`option${index + 1}`] === optionValue)
    );

    if (exactMatch) return exactMatch;

    return (
      variants
        .filter((variant) => variant[`option${position}`] === value)
        .sort((left, right) => {
          const leftScore = selectedOptions.reduce(
            (score, optionValue, index) => score + (optionValue && left[`option${index + 1}`] === optionValue ? 1 : 0),
            0
          );
          const rightScore = selectedOptions.reduce(
            (score, optionValue, index) => score + (optionValue && right[`option${index + 1}`] === optionValue ? 1 : 0),
            0
          );

          if (left.available !== right.available) {
            return Number(right.available) - Number(left.available);
          }

          return rightScore - leftScore;
        })[0] || null
    );
  }

  #applyVariantToCard(card, variant) {
    card.dataset.selectedOption1 = variant.option1 || '';
    card.dataset.selectedOption2 = variant.option2 || '';
    card.dataset.selectedOption3 = variant.option3 || '';

    const variants = this.#getCardVariants(card);
    const selectedOptions = this.#getCardSelectedOptions(card);

    card.querySelectorAll('[data-shop-look-option-button]').forEach((button) => {
      const position = Number(button.dataset.optionPosition || '0');
      const optionValue = button.dataset.optionValue || '';
      const isActive = position > 0 && selectedOptions[position - 1] === optionValue;

      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));

      if (position > 0) {
        const testOptions = [...selectedOptions];
        testOptions[position - 1] = optionValue;

        const exactMatch = variants.find((v) =>
          testOptions.every((val, index) => !val || v[`option${index + 1}`] === val)
        );

        if (!exactMatch || !exactMatch.available) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
      }
    });

    const swatchLabel = card.querySelector('[data-shop-look-swatch-label]');
    const colorOptionPosition = Number(card.dataset.colorOptionPosition || '0');
    if (swatchLabel && colorOptionPosition > 0) {
      swatchLabel.textContent = variant[`option${colorOptionPosition}`] || '';
    }

    const variantIdInput = card.querySelector('input[name="id"]');
    if (variantIdInput) {
      variantIdInput.value = `${variant.id}`;
      variantIdInput.disabled = !variant.available;
    }

    const addToCartButton = card.querySelector('[ref="addToCartButton"]');
    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !variant.available;
    }

    const addToCartComponent = card.querySelector('add-to-cart-component');
    if (addToCartComponent && variant.featured_image) {
      addToCartComponent.setAttribute('data-product-variant-media', variant.featured_image);
    }

    const price = card.querySelector('[data-shop-look-price]');
    if (price) {
      price.innerHTML = variant.price || '';
    }

    const inventory = card.querySelector('[data-shop-look-inventory]');
    if (inventory) {
      const lowInventory = variant.available && variant.inventory_quantity > 0 && variant.inventory_quantity <= 5;
      inventory.hidden = !lowInventory;
      const template = inventory.dataset.lowInventoryTemplate || '[count] left';
      inventory.textContent = lowInventory ? template.replace('[count]', `${variant.inventory_quantity}`) : '';
    }

    const cardImage = card.querySelector('.shop-the-look-hotspot__card-image');
    if (cardImage instanceof HTMLImageElement && variant.featured_image) {
      cardImage.src = variant.featured_image;
      cardImage.srcset = variant.featured_image;
      cardImage.alt = variant.featured_image_alt || cardImage.alt;
    }
  }

  #setInitialState() {
    // Prefer the first trigger whose corresponding card has product content.
    // Simpler: just activate the first trigger we find.
    const firstTrigger = this.#getTriggers()[0];
    if (!firstTrigger) return;

    const hotspotId = firstTrigger.dataset.shopLookTrigger;
    if (hotspotId) this.setActiveHotspot(hotspotId);
  }

  #handleClick = (event) => {
    if (event.target instanceof Element && event.target.closest('quick-add-component')) {
      return;
    }

    const optionButton = event.target instanceof Element ? event.target.closest('[data-shop-look-option-button]') : null;
    if (optionButton) {
      event.preventDefault();

      const card = optionButton.closest('[data-shop-look-card]');
      const position = Number(optionButton.dataset.optionPosition || '0');
      const value = optionButton.dataset.optionValue || '';

      if (!card || !position || !value) return;

      const variant = this.#findBestVariant(card, position, value);
      if (variant) {
        this.#applyVariantToCard(card, variant);
      }
      return;
    }

    const trigger = event.target instanceof Element ? event.target.closest('[data-shop-look-trigger]') : null;
    if (!trigger) return;

    event.preventDefault();

    const hotspotId = trigger.dataset.shopLookTrigger;
    if (!hotspotId) return;

    // If it's a link and it's already active, let it navigate
    if (trigger.tagName === 'A' && trigger.classList.contains('is-active')) {
      // Allow default navigation
      window.location.href = trigger.href;
      return;
    }

    this.setActiveHotspot(hotspotId);
  };

  setActiveHotspot(hotspotId, preventScroll = false) {
    const triggers = this.#getTriggers();
    const cards = this.#getCards();

    triggers.forEach((trigger) => {
      const isActive = trigger.dataset.shopLookTrigger === hotspotId;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-pressed', String(isActive));
    });

    cards.forEach((card) => {
      const isActive = card.dataset.shopLookCard === hotspotId;
      card.classList.toggle('is-active', isActive);
      card.hidden = !isActive;
    });

    if (!preventScroll) {
      // Scroll mobile picker tile into view
      const activeTile = this.querySelector(`.shop-the-look-hotspot__mobile-tile[data-shop-look-trigger="${hotspotId}"]`);
      const mobilePicker = this.querySelector('.shop-the-look__mobile-picker');
      if (activeTile && mobilePicker && mobilePicker.offsetParent !== null) {
        mobilePicker.scrollTo({
          left: activeTile.offsetLeft - (mobilePicker.clientWidth / 2) + (activeTile.clientWidth / 2),
          behavior: 'smooth'
        });
      }
    }
  }
}

customElements.define('product-hotspots-section', ProductHotspotsSection);
